import { FlowOrigin } from './../shared/storage.service';
import { Component, OnInit, TemplateRef, ViewChild, Input, AfterViewInit, OnDestroy } from '@angular/core';
import {
  BaseControl,
  Const,
  TaskCompletionParams,
  Tarea,
  DynamicFormComponent,
  TareaFinalizacion,
  TareaOpcionAdmin,
  TypeProcess,
  VistaDinamica,
  TareaRol,
  Disparador
} from '@app/shared';
import { FormService } from './shared/form.service';
import { ManageTaskService } from '../shared/manage-task.service';
import { filter, find, result } from 'lodash';
import { Subscription } from 'rxjs/Subscription';
import { generate } from 'shortid';
import { NotifierService } from '@app/shared/services/notifier.service';
import { Subject } from 'rxjs';

export interface NotifyCompletion {
  id: string;
  targetTask?: Tarea;
}

@Component({
  selector: 'iw-manage-completion',
  templateUrl: './manage-completion.component.html',
  styleUrls: ['./manage-completion.component.scss'],
  providers: [FormService]
})
export class ManageCompletionComponent implements OnInit, AfterViewInit, OnDestroy {
  // Contiene el tipo de proceso que se esta renderizando
  @Input() typeProcess: TypeProcess;
  // Tarea actual, con la que se esta trabajando (origen)
  @Input() sourceTask: Tarea;
  // Origen del flujo
  @Input() origin: FlowOrigin;
  // index de la finalizacion
  @Input() indexCompletion = 0;
  // Tarea elegida en el flujo
  targetTask: Tarea;
  // Estas propiedad aplica solo para las tareas con condiciones:
  // Indice de la condición que esta renderizando el componente.
  @Input() indexCondition: number;
  // Opción con la que se esta trabajando.
  @Input() option: TareaOpcionAdmin;
  // Todos los roles del sistema compatibles con las tareas
  @Input() roles: TareaRol[] = [];
  // Obtiene las asociaciones de la tarea
  @Input()
  set listAssociations(list: any[]) {
    this._listAssociations = list;
    if (this.dyFormRef.controls && this.dyFormRef.controls.length) {
      const valSelection = this.dyFormRef.form.controls['aliasAsociaciones'].value;
      if (valSelection) {
        const resultVal = find(list, { alias: valSelection });
        if (!resultVal) {
          this.dyFormRef.form.controls['aliasAsociaciones'].setValue(null);
        }
      }
    }
  }
  // Controles para el formulario dinámico
  controls: BaseControl<any>[] = [];
  // Lista de parametros validos para la key 'parametros' de una finalización de una tarea
  params: TaskCompletionParams[];
  // Controla si se muestra la tarea de destino
  showTargetTask = false;
  // Claves a ignorar en los parametros de la finalizacion
  paramskeysException: Array<string> = [
    'finalizacion',
    'estado_tramite',
    'estado_expediente',
    'responsable_tarea_asociada',
    'auto_guardado',
    'enviar_a_vista',
    'validar_sede',
    'grupos_responsables_asignacion',
    'guardar_usuario_responsable_persistente',
    'usuario_persistente',
    'validar_jurisdiccion',
    'disparar_servicio',
    'crear_rango_tiempo',
  ];
  // Destruye y gestiona el memory leak
  private destroy: Subject<void> = new Subject<void>();
  // Referencia del control personalizado para el select de tipos
  @ViewChild('selectTypesTpl') selectTypesTpl: TemplateRef<any>;
  // Referencia del control personalizado para el select de parametros
  @ViewChild('selectParamsTpl') selectParamsTpl: TemplateRef<any>;
  // Referencia del control personalizado para el select de los estados de el tramite
  @ViewChild('selectStatusTpl') selectStatusTpl: TemplateRef<any>;
  // Referencia del control personalizado para el select de los estados de el expediente
  @ViewChild('selectStatusExpedientTpl') selectStatusExpedientTpl: TemplateRef<any>;
  // Referencia del control personalizado para el select de los alias de las tareas asociaciadas
  @ViewChild('selectAliasAssociationTaskTpl') selectAliasAssociationTaskTpl: TemplateRef<any>;
  // Referencia del control personalizado para el select de las vistas dinamicas
  @ViewChild('selectDynamicViews') selectDynamicViews: TemplateRef<any>;
  // Referencia del control personalizado para el select de las sedes
  @ViewChild('selectSedes') selectSedes: TemplateRef<any>;
  // Referencia del control personalizado para el select de roles en tareas
  @ViewChild('selectRolesTpl') selectRolesTpl: TemplateRef<any>;
  // Referencia del control personalizado para el select de disparadores
  @ViewChild('selectTriggers') selectTiggresTpl: TemplateRef<any>;
  // Referencia al formulario dinámico
  @ViewChild(DynamicFormComponent) dyFormRef: DynamicFormComponent;
  // Suscripción a las notificaciones otros componentes
  private notifications: Subscription;
  // Identificador único para la comunicación con el flujo
  private id: string = generate();
  // Contiene las asociaciones de la tarea actual
  private _listAssociations: any[] = [];

  constructor(private ns: NotifierService, private fs: FormService, public mts: ManageTaskService) {
    this.params = filter(Const.TASK_COMPLETION_PARAMS, { required: false });
  }

  ngOnInit() {
    this.init();
  }

  async ngAfterViewInit() {
    // Vigilar el tipo de finalización para activar la
    // caja de selección de la tarea de finalización.
    await this.dyFormRef.form.get('tipo_finalizacion').valueChanges.subscribe((change: any) => {
      this.showTargetTask = this.showTargetTaskFn(change);
    });
    if (this.showTargetTask) {
      if (this.option) {
        this.targetTask = await this.mts.getCompletionTask(
          this.sourceTask,
          this.origin,
          this.indexCompletion,
          this.option
        );
      } else {
        this.targetTask = await this.mts.getCompletionTask(
          this.sourceTask,
          this.origin,
          this.indexCompletion
        );
      }
    }
    // Vigilar notificaciones de 'processAutomation' para obtener
    // la tarea de finalización escogida por el usuario.
    this.notifications = this.ns
      .get()
      .filter((n, i) => {
        return n.source === 'processAutomation' && n.action === 'setTaskTarget' && n.instance === this.typeProcess;
      })
      .subscribe(n => {
        const data = <NotifyCompletion>n.data;
        if (this.id === data.id) {
          this.targetTask = data.targetTask;
          this.showTargetTask = true;
        }
      });
  }

  ngOnDestroy() {
    if (this.notifications) {
      this.notifications.unsubscribe();
    }
    this.destroy.next();
    this.destroy.complete();
  }

  init() {
    // Solo funciona cuando se esta editando una tarea con finalizaciones
    const types = Const.TYPES_TASK;
    let completion: TareaFinalizacion = null;
    const completions = <TareaFinalizacion[]>result(this.sourceTask, 'tarea_finalizacion');
    let completionType = null;
    let params: string[] = [];
    let status = 0;
    let statusExpedient = 0;
    let optionId = 0;
    let responsibleTaskAssociation = '';
    let autoSaved: string | false = false;
    const sendView: number[] = [];
    let sede: number | false = false;
    let groupsResponsiblesAssignment: number | false = false;
    let savePersistentResponsible: string | false;
    let persistentUser: string | false;
    let validateJurisdiction: boolean;
    let triggers: number[] = [];
    let crearRangoTiempo: boolean = false;

    if (result(this.sourceTask, 'tarea_tipo.nombre')) {
      switch (this.sourceTask.tarea_tipo.nombre) {
        case types.form:
          if (this.option) {
            optionId = <number>result(this.option, 'id_opcion');
            if (this.indexCompletion) {
              if (completions[this.indexCompletion].opcion === optionId) {
                completion = completions[this.indexCompletion];
              } else {
                completion = find(completions, { opcion: optionId });
              }
            } else {
              completion = find(completions, { opcion: optionId });
            }
          } else {
            completion = completions[this.indexCompletion];
          }
          break;
        case types.none:
        case types.microProcess:
          completion = completions[this.indexCompletion];
          break;
        case types.condition:
          optionId = <number>result(this.option, 'id_opcion');
          if (this.indexCompletion) {
            if (completions[this.indexCompletion].opcion === optionId) {
              completion = completions[this.indexCompletion];
            } else {
              completion = find(completions, { opcion: optionId });
            }
          } else {
            completion = find(completions, { opcion: optionId });
          }
          break;
      }
    }

    if (completion) {
      params = Object.keys(completion.parametros).filter(item => {
        // tslint:disable-next-line: max-line-length
        // return item !== 'finalizacion' && item !== 'estado_tramite' && item !== 'estado_expediente' && item !== 'responsable_tarea_asociada';
        if (this.paramskeysException.indexOf(item) === -1) {
          return item;
        }
      });
      if (completion.parametros['estado_tramite']) {
        status = completion.parametros['estado_tramite'];
      }
      if (completion.parametros['estado_expediente']) {
        statusExpedient = completion.parametros['estado_expediente'];
      }
      if (completion.parametros['responsable_tarea_asociada']) {
        responsibleTaskAssociation = completion.parametros['responsable_tarea_asociada'];
      }
      if (completion.parametros['auto_guardado']) {
        autoSaved = completion.parametros['auto_guardado'];
      }
      if (completion.parametros['enviar_a_vista']) {
        completion.parametros['enviar_a_vista'].forEach((view: any) => {
          sendView.push(view.id);
        });
      }
      if (completion.parametros['validar_sede']) {
        sede = completion.parametros['validar_sede'];
      }
      if (completion.parametros['grupos_responsables_asignacion']) {
        groupsResponsiblesAssignment = completion.parametros['grupos_responsables_asignacion'];
      }
      if (completion.parametros['guardar_usuario_responsable_persistente']) {
        savePersistentResponsible = completion.parametros['guardar_usuario_responsable_persistente'];
      }
      if (completion.parametros['usuario_persistente']) {
        persistentUser = completion.parametros['usuario_persistente'];
      }
      if (completion.parametros['validar_jurisdiccion']) {
        validateJurisdiction = completion.parametros['validar_jurisdiccion'];
      }
      if (completion.parametros['disparar_servicio'] && completion.parametros['disparar_servicio'].length) {
        completion.parametros['disparar_servicio'].forEach((trigger: any) => {
          triggers.push(trigger.id);
        });
      }
      if (completion.parametros['crear_rango_tiempo']) {
        crearRangoTiempo = completion.parametros['crear_rango_tiempo'] ? true : false;
      }
      completionType = completion.tipo_finalizacion;
      this.showTargetTask = this.showTargetTaskFn(completionType);
    }
    // Generar campos del formulario por default
    this.controls = this.fs.getFields({
      selectTypesTpl: this.selectTypesTpl,
      selectStatusTpl: this.selectStatusTpl,
      tipo_finalizacion: completionType,
      selectParamsTpl: this.selectParamsTpl,
      selectDynamicViews: this.selectDynamicViews,
      selectSedes: this.selectSedes,
      selectRolesTpl: this.selectRolesTpl,
      selectTiggresTpl: this.selectTiggresTpl,
      parametros: params,
      estado_tramite: status,
      estado_expediente: statusExpedient,
      enviar_a_vista: sendView,
      selectStatusExpedientTpl: this.selectStatusExpedientTpl,
      selectAliasAssociationTaskTpl: this.selectAliasAssociationTaskTpl,
      responsable_tarea_asociada: responsibleTaskAssociation,
      auto_guardado: autoSaved,
      validar_sede: sede,
      id_opcion_saltar_area: this.mts.idCompletionSkipTask,
      grupos_responsables_asignacion: groupsResponsiblesAssignment,
      guardar_usuario_responsable_persistente: savePersistentResponsible,
      usuario_persistente: persistentUser,
      validar_jurisdiccion: validateJurisdiction,
      triggers: triggers,
      crear_rango_tiempo: crearRangoTiempo,
    }, this.indexCompletion);
  }

  get listAssociations(): any[] {
    return this._listAssociations;
  }

  /**
   * Genera una finalización compatiple con la tarea.
   *
   * @returns {TareaFinalizacion}
   * @memberof ManageCompletionComponent
   */
  getCompletion(): TareaFinalizacion {
    const completion = new TareaFinalizacion({
      tarea_inicio: this.sourceTask ? this.sourceTask.id : 0,
      tarea_destino: this.showTargetTask ? this.targetTask.id : 0,
      parametros: this.structureParams(),
      tipo_finalizacion: this.dyFormRef.form.value.tipo_finalizacion
    });
    return completion;
  }

  /**
   * Combierte el array de string con los parametros del formulario
   * dinámico y lo estructura de forma compatible con la key 'parametros'
   * de la finalización de una tarea.
   *
   * @returns
   * @memberof ManageCompletionComponent
   */
  structureParams() {
    const params = <string[]>this.dyFormRef.form.value.parametros;
    const requiredParams = filter(Const.TASK_COMPLETION_PARAMS, { required: true });
    const structureParams = {};
    params.map(el => {
      return (structureParams[el] = true);
    });
    requiredParams.map(el => {
      return (structureParams[el.name] = true);
    });
    if (Boolean(this.dyFormRef.form.value.cambioEstado)) {
      structureParams['estado_tramite'] = this.dyFormRef.form.value.estado;
    }
    if (Boolean(this.dyFormRef.form.value.cambioEstadoExpediente)) {
      structureParams['estado_expediente'] = this.dyFormRef.form.value.estadoExpediente;
    }
    if (Boolean(this.dyFormRef.form.value.responsableTareaAsociada)) {
      structureParams['responsable_tarea_asociada'] = this.dyFormRef.form.value.aliasAsociaciones;
    }
    if (Boolean(this.dyFormRef.form.value.autoGuardado)) {
      structureParams['auto_guardado'] = this.dyFormRef.form.value.txtMensajeEjecucion;
    }
    if (Boolean(this.dyFormRef.form.value.enviarVista)) {
      const sendView: any[] = [];
      this.dyFormRef.form.value.vistaDestino.forEach((id: number) => {
        const view: VistaDinamica = find(this.mts.dynamicViews, { id: id });
        if (view) {
          sendView.push({
            id: view.id,
            nombre: view.nombre
          });
        }
      });
      if (sendView.length) {
        structureParams['enviar_a_vista'] = sendView;
      }
      // structureParams['enviar_a_vista'] = this.dyFormRef.form.value.vistaDestino;
    }
    if (Boolean(this.dyFormRef.form.value.validarSede)) {
      structureParams['validar_sede'] = this.dyFormRef.form.value.cboSede;
    }
    if (Boolean(this.dyFormRef.form.value.asignarGrupoResponsable)) {
      structureParams['grupos_responsables_asignacion'] = this.dyFormRef.form.value.cboGrupoResponsable;
    }
    if (Boolean(this.dyFormRef.form.value.guardarResponsablePersistente)) {
      // tslint:disable-next-line: max-line-length
      structureParams['guardar_usuario_responsable_persistente'] = this.dyFormRef.form.value.txtAliasGuardarResponsablePersistente;
    }
    if (Boolean(this.dyFormRef.form.value.asignarResponsablePersistente)) {
      structureParams['usuario_persistente'] = this.dyFormRef.form.value.txtAliasUsuarioPersistente;
    }
    if (Boolean(this.dyFormRef.form.value.validarJurisdiccion)) {
      structureParams['validar_jurisdiccion'] = this.dyFormRef.form.value.validarJurisdiccion;
    }
    if (Boolean(this.dyFormRef.form.value.diparadores)) {
      const triggers: any[] = [];
      this.dyFormRef.form.value.cboDisparadores.forEach((id: number) => {
        const trigger: Disparador = find(this.mts.triggers, { id: id });
        if (trigger) {
          triggers.push({
            id: trigger.id,
            nombre: trigger.nombre
          });
        }
      });
      if (triggers.length) {
        structureParams['disparar_servicio'] = triggers;
      }
    }
    if (Boolean(this.dyFormRef.form.value.crearRangoTiempo)) {
      structureParams['crear_rango_tiempo'] = this.dyFormRef.form.value.crearRangoTiempo;

    }
    return structureParams;
  }

  /**
   * Determina si la información recogida es valida para crear una finalización.
   *
   * @returns {boolean}
   * @memberof ManageCompletionComponent
   */
  isValidData(): boolean {
    const isValid = this.showTargetTask ? this.dyFormRef.form.valid && !!this.targetTask : this.dyFormRef.form.valid;
    this.dyFormRef.validateControls(true);
    return isValid;
  }

  /**
   * Determina si el componente fue usado.
   *
   * @returns {boolean}
   * @memberof ManageCompletionComponent
   */
  isUntouched(): boolean {
    const isUntouched = this.showTargetTask
      ? this.dyFormRef.form.untouched && (!!this.targetTask === false ? true : false)
      : this.dyFormRef.form.untouched;
    return isUntouched;
  }

  /**
   * Resetea el componenete.
   *
   * @memberof ManageCompletionComponent
   */
  reset() {
    this.dyFormRef.reset();
    this.targetTask = null;
  }

  /**
   * Filtra en los tipos de finalizaciones(con el parametro id) y verifica
   * si el obj filtrado es 'saltar_tarea', de ser asi muestra la tarea de
   * destino.
   *
   * @private
   * @param {number} id
   * @memberof ManageCompletionComponent
   */
  showTargetTaskFn(id: number) {
    const typeCompletion = find(this.mts.completionTypes, { id: id });
    return result(typeCompletion, 'nombre') === 'saltar_tarea' ? true : false;
  }

  /**
   * Notifica al flujo para que entre en modo de finalización
   * para permitir al usuario escoger la tarea de finalización.
   *
   * @memberof ManageCompletionComponent
   */
  activeCompletionMode() {
    this.ns.set({
      source: 'manageCompletion',
      eventTriggered: 'click',
      action: 'activeCompletionMode',
      instance: this.typeProcess,
      data: <NotifyCompletion>{
        id: this.id,
        dataForm: this.dyFormRef.form.value
      }
    });
  }

  /**
   * Limpiar componente
   *
   * @memberof ManageCompletionComponent
   */
  empty() {
    this.dyFormRef.reset();
    this.dyFormRef.form.controls['parametros'].setValue([]);
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/manage-completion/manage-completion.component.ts